<template>
  <div class="flex flex-1 flex-col" :class="variant === 'sheet' && 'p-3'">
    <div
      class="relative flex-1 overflow-hidden bg-gray-800"
      :class="[
        variant === 'sheet' && 'rounded-[28px]',
        checker.capturing.value && 'ring-2 ring-[#ff3034] ring-inset',
      ]"
    >
      <video
        v-show="live"
        ref="video"
        autoplay
        muted
        playsinline
        class="absolute inset-0 size-full object-cover"
      />

      <span
        v-for="corner in CORNERS"
        :key="corner"
        class="pointer-events-none absolute z-10 size-[39px] border-[3px]"
        :class="[corner, live ? 'border-white' : 'border-white/30']"
        aria-hidden="true"
      />

      <div
        v-if="permission"
        class="absolute inset-x-7 top-1/2 z-20 -translate-y-1/2"
      >
        <CheckerPermission :kind="permission" dark />
      </div>

      <div
        class="absolute inset-x-0 z-20 flex flex-col items-center"
        :class="variant === 'modal' ? 'bottom-[23px]' : 'bottom-7'"
      >
        <Transition
          enter-active-class="transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)]"
          enter-from-class="scale-0"
          leave-active-class="transition-transform duration-150 ease-in"
          leave-to-class="scale-0"
        >
          <div v-if="checker.findingCount.value" class="mb-4">
            <CheckerMatchesPill
              :count="checker.findingCount.value"
              @review="checker.review"
            />
          </div>
        </Transition>
        <button
          v-if="live"
          type="button"
          :aria-label="
            checker.capturing.value ? 'Stop capture' : 'Start capture'
          "
          :disabled="
            !ready ||
            checker.live.value?.phase === 'draining' ||
            checker.requesting.value
          "
          class="flex size-[66px] cursor-pointer items-center justify-center rounded-full border border-white/35 bg-gray-900/55 transition-opacity hover:opacity-80 disabled:pointer-events-none"
          @click="checker.toggleLive(true)"
        >
          <span
            class="bg-[#ff3034] transition-all duration-200"
            :class="
              checker.capturing.value
                ? 'size-[34px] rounded-[4px]'
                : 'size-[56px] rounded-full'
            "
          />
        </button>
        <span
          class="mt-1.5 text-[14px] leading-[18px] tracking-[0.5px] uppercase [text-shadow:0_0_4px_rgba(0,0,0,0.5)]"
          :class="live ? 'text-white' : 'text-white/30 [text-shadow:none]'"
        >
          {{ checker.capturing.value ? 'Capturing...' : 'Visual capture' }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'

import CheckerMatchesPill from './CheckerMatchesPill.vue'
import CheckerPermission from './CheckerPermission.vue'
import { useChecker } from './useChecker'

defineProps<{ variant: 'modal' | 'sheet' }>()

const CORNERS = [
  'top-7 left-7 rounded-tl-[15px] border-r-0 border-b-0',
  'top-7 right-7 rounded-tr-[15px] border-b-0 border-l-0',
  'bottom-7 left-7 rounded-bl-[15px] border-t-0 border-r-0',
  'right-7 bottom-7 rounded-br-[15px] border-t-0 border-l-0',
]

const checker = useChecker()
const video = ref<HTMLVideoElement>()
const ready = ref(false)

const live = computed(() => !!checker.cameraStream.value)

const permission = computed(() => {
  if (checker.camera.value === 'denied') return 'camera'
  if (live.value && checker.microphone.value === 'denied') return 'microphone'
  return null
})

const handleReady = () => {
  ready.value = true
}

watch(
  [video, () => checker.cameraStream.value],
  ([element, stream]) => {
    checker.setVideo(element ?? null)
    if (!element) return
    ready.value = false
    element.srcObject = stream
    element.onloadeddata = handleReady
    if (stream) void element.play().catch(() => {})
  },
  { immediate: true }
)

onBeforeUnmount(() => checker.setVideo(null))
</script>
